/**
 * Clinical Trials Tabs Custom Element
 * Handles tab switching for clinical trials content
 */
class ClinicalTrialsTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll('.clinical-trials__tab');
    this.panes = this.querySelectorAll('.clinical-trials__pane');
    this.currentTab = 0;

    if (this.tabs.length > 1) {
      this.setupTabs();
      this.setupKeyboardNavigation();
    }
  }

  /**
   * Set up tab click handlers and initial state
   */
  setupTabs() {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectTab(index);
      });

      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
  }

  /**
   * Set up keyboard navigation for accessibility
   */
  setupKeyboardNavigation() {
    this.addEventListener('keydown', (event) => {
      let newIndex = this.currentTab;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          newIndex = this.currentTab > 0 ? this.currentTab - 1 : this.tabs.length - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          newIndex = this.currentTab < this.tabs.length - 1 ? this.currentTab + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = this.tabs.length - 1;
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this.announceTabChange();
          return;
        default:
          return;
      }

      this.selectTab(newIndex);
      this.tabs[newIndex].focus();
    });
  }

  /**
   * Select a tab and show corresponding pane
   * @param {number} index - Index of tab to select
   */
  selectTab(index) {
    if (index < 0 || index >= this.tabs.length) return;

    const previousTab = this.currentTab;
    this.currentTab = index;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('clinical-trials__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive.toString());
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panes.forEach((pane, i) => {
      const isActive = i === index;
      pane.classList.toggle('clinical-trials__pane--active', isActive);
      pane.hidden = !isActive;

      if (isActive) {
        pane.setAttribute('tabindex', '0');
      } else {
        pane.removeAttribute('tabindex');
      }
    });

    if (previousTab !== index) {
      this.announceTabChange();
    }

    this.trackTabClick(index);
  }

  /**
   * Announce tab change to screen readers
   */
  announceTabChange() {
    const activeTab = this.tabs[this.currentTab];
    const activePaneId = activeTab.getAttribute('aria-controls');
    const activePane = document.getElementById(activePaneId);

    if (activePane) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = `${activeTab.textContent} tab selected`;

      document.body.appendChild(announcement);

      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }

  /**
   * Track tab click for GTM analytics
   * @param {number} index - Index of clicked tab
   */
  trackTabClick(index) {
    const tabName = this.tabs[index]?.textContent?.trim();
    const productId = this.closest('[data-product-id]')?.dataset.productId;
    const layoutType = this.closest('.clinical-trials')?.dataset.layout;

    if (typeof publish === 'function') {
      publish('clinical_trials_tab_click', {
        tab_name: tabName,
        tab_index: index,
        product_id: productId,
        layout_type: layoutType,
        section_type: 'clinical_trials'
      });
    }

    if (window.dataLayer && tabName) {
      window.dataLayer.push({
        event: 'clinical_trials_tab_click',
        clinical_trials: {
          tab_name: tabName,
          tab_index: index,
          product_id: productId || '',
          layout_type: layoutType || 'unknown'
        }
      });
    }
  }
}

if (!customElements.get('clinical-trials-tabs')) {
  customElements.define('clinical-trials-tabs', ClinicalTrialsTabs);
}

document.addEventListener('DOMContentLoaded', function() {
  const clinicalTrialsElements = document.querySelectorAll('.clinical-trials');

  if (clinicalTrialsElements.length > 0 && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const layoutType = element.dataset.layout;
          const productId = element.closest('[data-product-id]')?.dataset.productId;
          const hasTabs = element.querySelectorAll('.clinical-trials__tab').length > 1;

          if (typeof publish === 'function') {
            publish('clinical_trials_view', {
              layout_type: layoutType,
              product_id: productId,
              has_tabs: hasTabs,
              section_type: 'clinical_trials'
            });
          }

          if (window.dataLayer) {
            window.dataLayer.push({
              event: 'clinical_trials_view',
              clinical_trials: {
                layout_type: layoutType || 'unknown',
                product_id: productId || '',
                has_tabs: hasTabs
              }
            });
          }

          observer.unobserve(element);
        }
      });
    }, {
      threshold: 0.5,
      rootMargin: '0px 0px -50px 0px'
    });

    clinicalTrialsElements.forEach(element => {
      observer.observe(element);
    });
  }
});